import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Plus, Trash2, Pencil, Loader2, Clock, Users, FlaskConical } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const emptyForm = { name: "", duration_days: 30, price: 0, credits: 1, connections: 1, is_trial: false };

export default function Plans() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin";
  const [plans, setPlans] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/plans"); setPlans(data); }
    catch { toast.error("Failed to load plans"); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, duration_days: p.duration_days, price: p.price, credits: p.credits ?? 1, connections: p.connections, is_trial: p.is_trial }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        duration_days: Number(form.duration_days),
        price: Number(form.price),
        credits: Number(form.credits),
        connections: Number(form.connections),
        is_trial: form.is_trial,
      };
      if (editing) { await api.put(`/plans/${editing.id}`, payload); toast.success("Plan updated"); }
      else { await api.post("/plans", payload); toast.success("Plan created"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    try { await api.delete(`/plans/${p.id}`); toast.success("Plan deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="plans-page">
      <PageHeader overline="Catalog" title="Subscription Plans" description="Define durations, pricing and connection limits.">
        {isAdmin && (
          <Button onClick={openCreate} data-testid="add-plan-button"><Plus className="h-4 w-4 mr-2" /> New Plan</Button>
        )}
      </PageHeader>

      {!plans ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-52 rounded-lg" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => (
            <Card key={p.id} className="p-6 border-border bg-card flex flex-col transition-transform duration-200 hover:-translate-y-0.5" data-testid={`plan-card-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className={`grid place-items-center h-10 w-10 rounded-md ${p.is_trial ? "bg-warning/10 text-warning" : "bg-primary/10 text-primary"}`}>
                  {p.is_trial ? <FlaskConical className="h-5 w-5" /> : <Package className="h-5 w-5" />}
                </div>
                {p.is_trial && <Badge variant="outline" className="border-warning/40 text-warning">Trial</Badge>}
              </div>
              <p className="mt-4 font-display font-semibold text-lg">{p.name}</p>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-display text-3xl font-bold tracking-tight">{p.is_trial ? "Free" : p.credits}</span>
                {!p.is_trial && <span className="text-sm text-muted-foreground">credit{p.credits === 1 ? "" : "s"}</span>}
              </div>
              <div className="mt-4 space-y-2 text-sm text-muted-foreground flex-1">
                <p className="flex items-center gap-2"><Clock className="h-4 w-4" /> {p.duration_days} days</p>
                <p className="flex items-center gap-2"><Users className="h-4 w-4" /> {p.connections} connection{p.connections > 1 ? "s" : ""}</p>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1 mt-4 pt-4 border-t border-border">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`plan-edit-${p.id}`}><Pencil className="h-4 w-4 mr-1.5" /> Edit</Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" data-testid={`plan-delete-${p.id}`}><Trash2 className="h-4 w-4 mr-1.5" /> Delete</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-popover">
                      <AlertDialogHeader><AlertDialogTitle>Delete {p.name}?</AlertDialogTitle>
                        <AlertDialogDescription>Existing customers keep their subscription.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(p)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Plan" : "New Plan"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Plan name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="6 Months" data-testid="plan-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Days</Label><Input type="number" value={form.duration_days} onChange={(e) => setForm((f) => ({ ...f, duration_days: e.target.value }))} data-testid="plan-days" /></div>
              <div className="space-y-2"><Label>Credits</Label><Input type="number" value={form.credits} onChange={(e) => setForm((f) => ({ ...f, credits: e.target.value }))} data-testid="plan-credits" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Price ($ retail)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} data-testid="plan-price" /></div>
              <div className="space-y-2"><Label>Connections</Label><Input type="number" value={form.connections} onChange={(e) => setForm((f) => ({ ...f, connections: e.target.value }))} data-testid="plan-conns" /></div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <Label className="cursor-pointer">Trial plan</Label>
              <Switch checked={form.is_trial} onCheckedChange={(v) => setForm((f) => ({ ...f, is_trial: v }))} data-testid="plan-trial" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving || !form.name} data-testid="plan-save">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
